/**
 * Shared ApexCharts theme for Native Analytics.
 *
 * Every chart in the dashboard should merge its options on top of
 * getBaseChartOptions() so the whole surface shares one visual language:
 * Inter typography (charts otherwise fall back to ApexCharts' default
 * Helvetica/Arial — the biggest "cheap" tell), a calm palette, soft dashed
 * gridlines, consistent tooltips/legends, and tabular figures.
 *
 * Usage:
 *   import { getBaseChartOptions, mergeChartOptions } from '../theme/apexTheme';
 *   const options = mergeChartOptions(getBaseChartOptions(), {
 *     chart: { type: 'bar' },
 *     xaxis: { categories },
 *   });
 */

import { COLORS, SERIES_PALETTE, FONT_FAMILY } from './tokens';

/** Deep-merge plain objects (arrays replaced, not concatenated). */
export const mergeChartOptions = (base, override = {}) => {
  if (Array.isArray(base) || Array.isArray(override)) return override;
  if (typeof base !== 'object' || base === null) return override;
  if (typeof override !== 'object' || override === null) return override;

  const out = { ...base };
  Object.keys(override).forEach(key => {
    out[key] =
      key in base ? mergeChartOptions(base[key], override[key]) : override[key];
  });
  return out;
};

/**
 * Base options applied to every chart.
 * @param {object} opts
 * @param {boolean} opts.sparkline - strip axes/grid/legend for inline sparklines
 */
export const getBaseChartOptions = ({ sparkline = false } = {}) => ({
  chart: {
    fontFamily: FONT_FAMILY,
    foreColor: COLORS.textMuted,
    toolbar: { show: false },
    zoom: { enabled: false },
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 350,
      animateGradually: { enabled: false },
    },
    sparkline: { enabled: sparkline },
  },
  colors: SERIES_PALETTE,
  grid: {
    show: !sparkline,
    borderColor: COLORS.gridline,
    strokeDashArray: 4,
    padding: { left: 8, right: 8 },
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
  },
  dataLabels: { enabled: false },
  stroke: {
    curve: 'smooth',
    width: 2.5,
    lineCap: 'round',
  },
  plotOptions: {
    bar: {
      borderRadius: 6,
      borderRadiusApplication: 'end',
      columnWidth: '55%',
    },
  },
  xaxis: {
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: {
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: '12px',
        colors: COLORS.textMuted,
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: '12px',
        colors: COLORS.textMuted,
      },
    },
  },
  legend: {
    show: !sparkline,
    position: 'bottom',
    horizontalAlign: 'left',
    fontFamily: FONT_FAMILY,
    fontSize: '13px',
    markers: { radius: 12 },
    itemMargin: { horizontal: 12, vertical: 4 },
  },
  tooltip: {
    enabled: true,
    style: { fontFamily: FONT_FAMILY, fontSize: '12px' },
    theme: 'light',
  },
  states: {
    hover: { filter: { type: 'lighten', value: 0.06 } },
    active: { filter: { type: 'darken', value: 0.1 } },
  },
});
